import React, { useState } from "react";
import style from "./integration-item-color-picker.module.css";
import { DropdownButton } from "../toolbar-buttons";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { getTextColorForBackgroundColor, replaceColorOpacity } from "frontend/utils/color-utils";
import { getSelectedStatus } from "frontend/utils/monday-utils";
import { ColorOpacityPicker, SolidColor } from "frontend/ui-components/color-opacity-picker";

type ColorPickerTabs = "color" | "syncedColor";

export function IntegrationItemColorPicker({
  value,
  onChange,
  multi,
  itemId,
  tooltip,
  documentId,
  colorPalette,
  customColors,
  allowCustomColor = false,
  allowSyncedColor = true,
}: {
  value?: any;
  onChange?: (value: any, addUndo: boolean) => void;
  multi?: boolean;
  documentId?: string;
  itemId?: string;
  colorPalette?: any;
  customColors?: any;
  withTransparency?: boolean;
  tooltip?: string;
  onlyOutline?: boolean;
  allowCustomColor?: boolean;
  allowSyncedColor?: boolean;
}) {
  const [selectedTab, setSelectedTab] = useState<ColorPickerTabs>(!multi && value.colorBy ? "syncedColor" : "color");
  const { getBoardIntegrationConfig, getItem } = useBoardTasks(documentId);
  const columns = getBoardIntegrationConfig(value.integrationId)?.columns;

  // Backwards compatibility for monday items with no fill
  if (value.fill === undefined) {
    value.fill = "#1973FF";
  }

  function renderPicker() {
    return (
      <div className={style.popup}>
        <div className={style.tabContainer}>
          {renderTab("Colors", "color")}
          {renderTab("Color by", "syncedColor")}
        </div>
        <div className={style.inputContainer}>
          {selectedTab === "color" && renderColorPalette()}
          {selectedTab === "syncedColor" && renderSyncedColorPicker()}
        </div>
      </div>
    );
  }

  function renderTab(tooltip: string, tabId: ColorPickerTabs) {
    const isSelected = selectedTab === tabId;

    return (
      <div className={!isSelected ? style.tab : style.tabSelected} onClick={() => setSelectedTab(tabId)}>
        {tooltip}
      </div>
    );
  }

  function renderSyncedColorPicker() {
    if (!allowSyncedColor) {
      return (
        <div className={style.flexContainer}>
          <div className={style.syncedPickerErrorPage}>
            <span>Items imported from different monday.com boards</span>
          </div>
        </div>
      );
    }

    const statusColumns = columns?.filter((col) => col.type === "status") ?? [];

    if (statusColumns.length === 0) {
      return (
        <div className={style.flexContainer}>
          <div className={style.syncedPickerErrorPage}>
            <span>No label columns found in board</span>
          </div>
        </div>
      );
    }

    return (
      <div className={style.syncedColorPicker}>
        <span>Auto sync the color of the selected column with this task</span>
        <div className={style.divider} />
        <div className={style.syncedColorPickerGrid}>
          {statusColumns.map((column) => {
            const isSelected = !multi && value.colorBy === column.id;
            return (
              <div
                key={column.id}
                className={style.syncedColorPickerItem}
                onClick={() => onChange && onChange({ fill: null, colorBy: column.id }, true)}
              >
                <div className={isSelected ? style.radioButtonSelected : style.radioButton}>
                  {isSelected && <div className={style.radioButtonInnerCircle} />}
                </div>
                <span>{column.title}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderColorPalette() {
    return (
      <ColorOpacityPicker
        value={value.fill}
        onChange={handleColorSelection}
        colorPalette={colorPalette}
        customColors={customColors}
        withTransparency={true}
        allowCustomColor={allowCustomColor}
      />
    );
  }

  function renderSyncedIcon(color: string = "#113357") {
    return (
      <svg width="15" height="15" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.9703 6.50548L11.8408 5.99871C11.5647 4.91785 10.954 3.9608 10.1064 3.27356C9.25897 2.58657 8.22203 2.20729 7.15591 2.1901C6.08993 2.17291 5.04976 2.51855 4.1952 3.17604C3.49954 3.71127 3.16113 4.23213 2.75087 5.07725L4.69782 4.849L4.81962 5.88797L1.13146 6.32034L0.688082 2.53824L1.72705 2.41644L1.9519 4.33438C2.34696 3.56487 2.77348 2.95001 3.55731 2.34695C4.59762 1.54655 5.86815 1.12312 7.17278 1.14415C8.47726 1.16519 9.73929 1.6293 10.7651 2.46097C11.7907 3.29237 12.5236 4.44498 12.8543 5.73979L12.9838 6.24656L11.9703 6.50548Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.95281 7.37598L2.02238 7.89437C2.17077 9.00004 2.66582 10.0217 3.42773 10.8029C4.18936 11.5839 5.17509 12.0814 6.23195 12.2226C7.28868 12.3637 8.36202 12.1416 9.28733 11.5881C10.0406 11.1375 10.4373 10.6595 10.9432 9.86795L8.98292 9.86795L8.98292 8.82186L12.6963 8.82187L12.6963 12.6299L11.6502 12.6299L11.6502 10.6988C11.1683 11.4171 10.6731 11.9781 9.82434 12.4858C8.69792 13.1596 7.38672 13.4322 6.09342 13.2594C4.80026 13.0867 3.60085 12.4788 2.67882 11.5333C1.75707 10.5881 1.16336 9.35805 0.98559 8.03352L0.916016 7.51513L1.95281 7.37598Z"
          fill={color}
        />
      </svg>
    );
  }

  function handleColorSelection(color: string | number, addUndo: boolean) {
    if (typeof color === "number") {
      color = replaceColorOpacity(value.fill, color);
    }
    onChange?.({ fill: color, colorBy: null }, addUndo);
  }

  function getItemColor(id: string, integrationId: string, columnId: string) {
    const item = getItem(id, integrationId);
    const status = getSelectedStatus(item?.columnValues, { id: columnId });
    return status.color;
  }

  const gradient = "conic-gradient(#ff4800, #dfd902, #20dc68, #0092f4, #da54d8, #ff4800)";
  let iconColor: string = value.fill ?? "transparent";
  let syncIconColor: string = "#113357";
  if (multi) {
    iconColor = gradient;
  } else if (value.colorBy) {
    iconColor = itemId ? getItemColor(itemId, value.integrationId, value.colorBy) : gradient;
    syncIconColor = getTextColorForBackgroundColor(iconColor);
  }

  return (
    <DropdownButton
      tooltip={tooltip ?? "Color"}
      renderPopup={() => renderPicker()}
      data-testid={"MondayItemColorPicker"}
      className={style.container}
    >
      <div className={style.dropdownIcon}>
        {<SolidColor color={iconColor} />}
        {!multi && value.colorBy && <div className={style.syncIcon}>{renderSyncedIcon(syncIconColor)}</div>}
      </div>
    </DropdownButton>
  );
}
